import * as THREE from 'three'
import type { ImportedModelTopologyHostTransformProjectionPort } from '../../../../types/imported-model/ImportedModelTopologyHostTransformProjection'
import type { FeatureResultMesh } from '../../../mesh/feature'
import type {
  ImportedModelInstancedMeshRegistry,
  ImportedModelInstancedMeshUnit,
} from '../mesh-unit'

/**
 * 将 imported topology pick host 接入正式实例 Unit 的普通对象变换投影。
 *
 * Repository 只依赖投影端口；本类负责通过活动 Registry 找到具体 Unit。普通或已经降级的
 * source Mesh 不存在活动 Unit，此时保持原场景父链，不创建额外绑定。
 */
export class ImportedModelInstancedTopologyHostTransformProjector
  implements ImportedModelTopologyHostTransformProjectionPort {
  /** 已从直接 source Mesh 临时提升到原业务父级的 topology host。 */
  private readonly promotedSourceMeshByPickHost = new Map<FeatureResultMesh, THREE.Mesh>()

  /** 迁移 topology host 时复用的 source 世界矩阵。 */
  private readonly sourceWorldMatrix = new THREE.Matrix4()

  /** 迁移 topology host 时复用的 host 世界矩阵。 */
  private readonly pickHostWorldMatrix = new THREE.Matrix4()

  /** 迁移 topology host 时复用的目标父级世界逆矩阵。 */
  private readonly inverseTargetParentWorldMatrix = new THREE.Matrix4()

  /** 迁移 topology host 时复用的目标局部矩阵。 */
  private readonly targetLocalMatrix = new THREE.Matrix4()

  /** 缓存态 topology host 相对 source Mesh 的稳定局部矩阵。 */
  private readonly pickHostSourceLocalMatrix = new THREE.Matrix4()

  /**
   * 创建实例 topology host 变换投影器。
   *
   * @param registry 当前 Viewer 独占的活动实例 Unit 注册表。
   */
  public constructor(
    private readonly registry: ImportedModelInstancedMeshRegistry,
  ) {}

  /**
   * 将 pick host 挂接到 source Mesh 对应的正式 Unit。
   *
   * @param sourceMesh 提供权威变换的原始 Mesh。
   * @param pickHost 需要跟随旋转、移动的拓扑宿主。
   * @param targetParent 普通显示态下承载该宿主的业务父对象。
   */
  public attach(
    sourceMesh: THREE.Mesh,
    pickHost: FeatureResultMesh,
    targetParent: THREE.Object3D,
  ): boolean {
    const unit = this.registry.resolveSourceMesh(sourceMesh)
    if (!unit) {
      return false
    }

    /**
     * 空闲宿主的 matrix 表达 source 局部坐标；活动宿主则可从当前世界矩阵反解同一后缀。
     * 先恢复这份稳定后缀，再用 Unit 的权威 source 世界矩阵绝对定位，禁止依赖已脱离场景的
     * source.matrixWorld 或反复 attach 产生累计变换。
     */
    const shouldPromotePickHost = unit.resolveOccurrenceRoot(sourceMesh) === sourceMesh
    const projectionParent = shouldPromotePickHost
      ? unit.resolveSourceMeshOriginalParent(sourceMesh)
      : targetParent
    if (
      !projectionParent
      || !this.copyPickHostSourceLocalMatrix(unit, sourceMesh, pickHost)
      || !unit.copySourceMeshWorldMatrix(sourceMesh, this.sourceWorldMatrix)
    ) {
      return false
    }
    this.pickHostWorldMatrix.multiplyMatrices(
      this.sourceWorldMatrix,
      this.pickHostSourceLocalMatrix,
    )
    this.reparentWithWorldMatrix(pickHost, projectionParent, this.pickHostWorldMatrix)
    if (shouldPromotePickHost) {
      this.promotedSourceMeshByPickHost.set(pickHost, sourceMesh)
    }

    try {
      const attached = unit.attachObjectTransformProjection(sourceMesh, pickHost)
      if (!attached && shouldPromotePickHost) {
        this.restoreDirectMeshPickHost(unit, sourceMesh, pickHost)
      }
      return attached
    } catch (error) {
      if (shouldPromotePickHost) {
        this.restoreDirectMeshPickHost(unit, sourceMesh, pickHost)
      }
      throw error
    }
  }

  /**
   * 将活动 topology host 规范化为 source 局部坐标后转入游离缓存态。
   *
   * @param sourceMesh 原始显示 Mesh。
   * @param pickHost 即将进入有界空闲缓存的拓扑宿主。
   * @returns source 与宿主矩阵均可解析并完成暂停时返回 true。
   */
  public suspend(sourceMesh: THREE.Mesh, pickHost: FeatureResultMesh): boolean {
    const unit = this.registry.resolveSourceMesh(sourceMesh)
    if (!unit || !this.copyPickHostSourceLocalMatrix(unit, sourceMesh, pickHost)) {
      return false
    }
    unit.detachObjectTransformProjection(sourceMesh, pickHost)
    pickHost.removeFromParent()
    /** 游离对象的 local matrix 明确定义为 source-local，供下一次绝对挂载直接消费。 */
    this.applyLocalMatrix(pickHost, this.pickHostSourceLocalMatrix)
    this.promotedSourceMeshByPickHost.delete(pickHost)
    return true
  }

  /**
   * 解除 pick host 与当前活动 Unit 的绑定。
   *
   * @param sourceMesh 原始显示 Mesh。
   * @param pickHost 即将释放或迁移的拓扑宿主。
   */
  public detach(sourceMesh: THREE.Mesh, pickHost: FeatureResultMesh): void {
    const unit = this.registry.resolveSourceMesh(sourceMesh)
    unit?.detachObjectTransformProjection(sourceMesh, pickHost)
    if (this.promotedSourceMeshByPickHost.get(pickHost) !== sourceMesh) {
      return
    }
    if (!unit || !this.restoreDirectMeshPickHost(unit, sourceMesh, pickHost)) {
      throw new Error(`实例 topology host 无法恢复到 source Mesh：${sourceMesh.uuid}`)
    }
  }

  /**
   * 把实例生命周期内提升的 topology host 恢复为直接 source Mesh 的子节点。
   *
   * @param unit 当前 source 所属实例 Unit。
   * @param sourceMesh 待恢复父子关系的业务 Mesh。
   * @param pickHost 待恢复的 topology host。
   * @returns 已保持世界变换完成恢复时返回 true。
   */
  private restoreDirectMeshPickHost(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    pickHost: FeatureResultMesh,
  ): boolean {
    if (this.promotedSourceMeshByPickHost.get(pickHost) !== sourceMesh) {
      return true
    }
    if (!pickHost.parent || !unit.copySourceMeshWorldMatrix(sourceMesh, this.sourceWorldMatrix)) {
      return false
    }
    pickHost.updateWorldMatrix(true, false)
    this.pickHostWorldMatrix.copy(pickHost.matrixWorld)
    this.inverseTargetParentWorldMatrix.copy(this.sourceWorldMatrix).invert()
    this.targetLocalMatrix.multiplyMatrices(
      this.inverseTargetParentWorldMatrix,
      this.pickHostWorldMatrix,
    )
    pickHost.removeFromParent()
    sourceMesh.add(pickHost)
    this.applyLocalMatrix(pickHost, this.targetLocalMatrix)
    this.promotedSourceMeshByPickHost.delete(pickHost)
    return true
  }

  /**
   * 读取 topology host 相对 source Mesh 的稳定局部矩阵。
   *
   * 游离缓存态和 source 子节点态直接保存 source-local；活动投影态则使用 Unit 提供的权威
   * source 世界矩阵反解。这样 source 已脱离场景树时也不会把旧父级局部矩阵误当 source-local。
   *
   * @param unit 当前 source 所属实例 Unit。
   * @param sourceMesh topology host 的本地坐标来源。
   * @param pickHost 待读取的 topology host。
   * @returns source-local 矩阵可解析时返回 true。
   */
  private copyPickHostSourceLocalMatrix(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    pickHost: FeatureResultMesh,
  ): boolean {
    if (!pickHost.parent || pickHost.parent === sourceMesh) {
      if (pickHost.matrixAutoUpdate) {
        pickHost.updateMatrix()
      }
      this.pickHostSourceLocalMatrix.copy(pickHost.matrix)
      return true
    }
    if (!unit.copySourceMeshWorldMatrix(sourceMesh, this.sourceWorldMatrix)) {
      return false
    }
    pickHost.updateWorldMatrix(true, false)
    this.inverseTargetParentWorldMatrix.copy(this.sourceWorldMatrix).invert()
    this.pickHostSourceLocalMatrix.multiplyMatrices(
      this.inverseTargetParentWorldMatrix,
      pickHost.matrixWorld,
    )
    return true
  }

  /**
   * 在保持给定世界矩阵的前提下迁移对象父级。
   *
   * @param object 待迁移对象。
   * @param targetParent 新父对象。
   * @param worldMatrix 迁移前已解析的权威世界矩阵。
   */
  private reparentWithWorldMatrix(
    object: THREE.Object3D,
    targetParent: THREE.Object3D,
    worldMatrix: THREE.Matrix4,
  ): void {
    targetParent.updateWorldMatrix(true, false)
    this.inverseTargetParentWorldMatrix.copy(targetParent.matrixWorld).invert()
    this.targetLocalMatrix.multiplyMatrices(
      this.inverseTargetParentWorldMatrix,
      worldMatrix,
    )
    object.removeFromParent()
    targetParent.add(object)
    this.applyLocalMatrix(object, this.targetLocalMatrix)
  }

  /**
   * 将已求得的局部矩阵写入 Object3D 并同步子树世界矩阵。
   *
   * @param object 接收局部变换的对象。
   * @param localMatrix 已换算到当前父级坐标系的局部矩阵。
   */
  private applyLocalMatrix(object: THREE.Object3D, localMatrix: THREE.Matrix4): void {
    localMatrix.decompose(object.position, object.quaternion, object.scale)
    object.updateMatrix()
    object.updateWorldMatrix(false, true)
  }
}
